'use client';

import { useCallback, useState, useEffect, type FC, useContext } from 'react';
import type {
  IncomingAddress,
  ProfileReviewDialogProps,
} from './ProfileReviewDialog.types';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Input,
  Checkbox,
  Text,
} from '@/_components';
import { updateCompany } from '@/_api/client-companies';
import { UserContext } from '@/_contexts/UserProvider';
import { Company } from '@/_types/models/company';
import { User } from '@/_types/models/user';
import { ToastContext } from '@/_contexts/ToastProvider';
import { AddressInput } from './_components/AddressInput';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getCountryData, TCountryCode } from 'countries-list';
import { deformatPhoneNumber, formatPhoneNumber } from '@/_utils/phoneNumbers';

const parseExistingEntityAddressData = (
  company: Company | undefined,
): IncomingAddress | undefined => {
  if (!company) return undefined;

  return {
    // at soraban, we are storing `street_number` and `route` combined as `street`
    route: company.street || null,
    locality: company.city || null,
    administrative_area_level_1: company.state || null,
    postal_code: company.zip ? parseInt(company.zip, 10) : null,
    country: company.country || null,
    // we aren't tracking the following: county, latitude, longitude
    administrative_area_level_2: '',
    latitude: 0,
    longitude: 0,
    // our `street` that we are assigning to `route` is a combination of
    // street number and route, so setting `street_number` to empty here
    street_number: '',
  };
};

const getFormattedAddress = (address: IncomingAddress): string =>
  [
    address.route,
    address.subpremise,
    address.locality,
    address.administrative_area_level_1,
    address.postal_code,
    address.country && getCountryData(address.country).name,
  ]
    .filter((part) => part)
    .join(', ');

export const ProfileReviewDialog: FC<ProfileReviewDialogProps> = ({
  isOpen,
  onClose,
  questionnaireId,
}) => {
  const { selectedCompanyId, user, token, reloadCompanies } =
    useContext(UserContext);
  const { firm } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [address, setAddress] = useState<IncomingAddress | undefined>(
    undefined,
  );
  const [agreedToReceiveTextMessages, setAgreedToReceiveTextMessages] =
    useState<boolean>(false);

  const onAgreeToReceiveTextMessages = () => {
    setAgreedToReceiveTextMessages(!agreedToReceiveTextMessages);
  };

  const onEmailChange = (event: any) => setEmail(event.target.value);
  const onPhoneChange = (event: any) => setPhone(event.target.value);
  const onAddressChange = (incomingAddress: IncomingAddress) =>
    setAddress(incomingAddress);

  const getStreetName = (
    address?: IncomingAddress,
    user?: User,
  ): string | null => {
    if (!user) return null;
    if (!address || !address?.route) {
      return user?.default_company?.street || null;
    }

    let streetAddress: string = '';

    if (address?.street_number) streetAddress += `${address?.street_number}`;
    if (address?.route) streetAddress += ` ${address?.route}`;
    if (address?.subpremise) streetAddress += ` ${address?.subpremise}`;

    return streetAddress;
  };

  const isValid = !!email;

  const onConfirm = useCallback(async () => {
    try {
      setLoading(true);
      const city: string | null =
        (address?.locality || user?.default_company?.city)?.trim() || null;
      const state: string | null =
        (
          address?.administrative_area_level_1 || user?.default_company?.state
        )?.trim() || null;
      const zip: string | null =
        (
          address?.postal_code?.toString() || user?.default_company?.zip
        )?.trim() || null;
      const street: string | null | undefined = getStreetName(
        address,
        user,
      )?.trim();
      const country: TCountryCode | null = address?.country || null;

      const preferredContactMethod = agreedToReceiveTextMessages
        ? 'sms'
        : 'email';

      await updateCompany({
        email,
        questionnaireId,
        companyId: selectedCompanyId!,
        preferred_contact_method: preferredContactMethod,
        phoneNumber: deformatPhoneNumber(phone),
        street,
        zip,
        city,
        state,
        country,
        userId: user?.id!,
        token,
      });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      reloadCompanies(token);
      onClose();
    }
  }, [
    addToast,
    user,
    email,
    onClose,
    reloadCompanies,
    phone,
    token,
    questionnaireId,
    address,
    agreedToReceiveTextMessages,
    selectedCompanyId,
  ]);

  useEffect(() => {
    setPhone(formatPhoneNumber(user?.phone_number || ''));
  }, [user?.phone_number]);

  useEffect(() => {
    setEmail(user?.email || '');
  }, [user?.email]);

  // If user has existing address data, use it
  useEffect(() => {
    setAddress(parseExistingEntityAddressData(user?.default_company));
  }, [user?.default_company]);

  useEffect(() => {
    setAgreedToReceiveTextMessages(user?.preferred_contact_method === 'sms');
  }, [user?.preferred_contact_method]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm this information"
      isPersistent={true}
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          <Alert type="info">
            Before proceeding, please review your contact information to make
            sure they&apos;re all up to date for your services. You can also
            edit this information under Profile page.
          </Alert>
          <Input
            value={email}
            onChange={onEmailChange}
            label="Primary Contact Email"
          />
          <Input
            value={phone}
            onChange={onPhoneChange}
            mask="(___) ___-____"
            placeholder="(000) 000-0000"
            label="Cell Phone Number"
          />
          <Flex isColumn={true} gap={8}>
            <Text>Don&apos;t want to miss any notifications?</Text>
            <Checkbox
              label="Send me notifications via SMS"
              isChecked={agreedToReceiveTextMessages}
              isDisabled={!phone}
              helperText={
                !phone
                  ? 'You must have a phone number to receive SMS notifications.'
                  : undefined
              }
              onChange={onAgreeToReceiveTextMessages}
            />
          </Flex>
          {agreedToReceiveTextMessages && (
            <Alert type="info" icon="info">
              By providing this phone number, you agree to receive messages from{' '}
              {firm?.name}. Message and data rates may apply. Message frequency
              varies.
            </Alert>
          )}
          <AddressInput
            id="address-change"
            initialValue={address && getFormattedAddress(address)}
            onChange={onAddressChange}
            label="Current Address"
          />
          <ButtonGroup align="right">
            <Button
              intent="primary"
              value="Confirm"
              onClick={onConfirm}
              isLoading={isLoading}
              isDisabled={isLoading || !isValid}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
